import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Map as LeafletMap } from "leaflet";
import { formatRating } from "@nhomi/core";
import { watchPlaces, type Place } from "@/data/places";
import { watchMembers, type Member } from "@/data/members";
import { photoURL } from "@/data/plates";
import { Empty, Spinner } from "@/ui/primitives";
import { StoragePhoto } from "@/ui/StoragePhoto";
import { IconBack, IconDetails, IconPin } from "@/ui/icons";
import { TabBar } from "@/ui/TabBar";

/* ============================================================
   Nosso mapa — onde a gente comeu

   Lista e mapa da mesma coisa, e agora é aba: entra pela barra de baixo,
   no lugar que era do bate-papo.

   O MAPA ABRE PRIMEIRO. Era a lista, de quando isto vivia dois toques
   abaixo de Detalhes e quem chegava aqui já sabia o que vinha buscar.
   Como aba, a primeira coisa na tela é o que dá nome a ela — e a lista
   é o mesmo dado sem a única coisa que só o mapa mostra: onde esses
   lugares ficam em relação uns aos outros.

   O mapa entra por import dinâmico porque são 150 KB. Antes isso era
   "só interessa a quem abre esta aba"; agora é quem abre a tela, mas o
   dinâmico continua valendo — os 150 KB saem do caminho de quem só abre
   o app pra postar, e a lista continua funcionando quando o mapa não
   carrega.
   ============================================================ */

export function Guide() {
  const { cid = "" } = useParams();
  const [places, setPlaces] = useState<Place[] | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [aba, setAba] = useState<"mapa" | "lista">("mapa");

  useEffect(() => watchPlaces(cid, setPlaces), [cid]);
  useEffect(() => watchMembers(cid, setMembers), [cid]);

  const primeiroNome = (uid: string) =>
    members.find((m) => m.uid === uid)?.name.split(" ")[0] ?? "";

  const comMapa = (places ?? []).filter((p) => p.coords);

  return (
    <main className="screen">
      <div className="topbar">
        <Link className="topbar-btn" to={`/c/${cid}`} aria-label="Voltar"><IconBack /></Link>
        <div className="topbar-title">Nosso mapa</div>
        <span className="topbar-btn" />
      </div>

      <div className="screen-body">
        <div className="page-head">
          <h1>Onde a gente comeu</h1>
          <p className="sub">
            {places?.length ?? 0} {places?.length === 1 ? "lugar" : "lugares"} do desafio
          </p>
        </div>

        {/* Mapa primeiro, e não por simetria com o estado inicial: a ordem
            do segmentado é a ordem de importância que a tela declara, e
            botão aceso na segunda posição contradiz isso em silêncio. */}
        <div className="segmented on-white">
          <button className={aba === "mapa" ? "active" : ""} onClick={() => setAba("mapa")}>
            <IconPin size={16} /> Mapa
          </button>
          <button className={aba === "lista" ? "active" : ""} onClick={() => setAba("lista")}>
            <IconDetails size={16} /> Lista
          </button>
        </div>

        {places === null ? <Spinner /> : places.length === 0 ? (
          <Empty emoji="📍" title="Nenhum lugar ainda">
            <p>
              Preencha o campo “Onde foi?” ao publicar um prato e o lugar aparece
              na Lista. Marque também o ponto no mapa e ele vira pino aqui.
            </p>
          </Empty>
        ) : aba === "lista" ? (
          <div className="card" style={{ padding: "6px 0" }}>
            {places.map((p) => (
              <div key={p.id} className="rank-row">
                <div className="checkin-thumb guia">
                  {p.thumbPath ? <StoragePhoto path={p.thumbPath} alt="" /> : <IconPin size={20} />}
                </div>
                <div className="rank-main">
                  <div className="rank-name">{p.name}</div>
                  <div className="rank-sub">
                    {p.visits} {p.visits === 1 ? "visita" : "visitas"}
                    {p.uids?.length
                      ? ` · ${p.uids.map(primeiroNome).filter(Boolean).join(", ")}`
                      : ""}
                  </div>
                </div>
                <div className="rank-pos guia">
                  <div>{p.rating != null ? `⭐ ${formatRating(p.rating)}` : "—"}</div>
                  {p.coords && <div className="rank-sub">no mapa</div>}
                </div>
              </div>
            ))}
          </div>
        ) : comMapa.length === 0 ? (
          /* Esta é a primeira tela de quem tem lugares mas nunca marcou
             ponto nenhum — antes ela era um beco: dizia o que fazer no
             futuro e não dizia que os lugares do grupo estão a um toque. */
          <Empty emoji="🗺️" title="Nenhum lugar com localização">
            <p>
              Os {places.length} {places.length === 1 ? "lugar" : "lugares"} do grupo
              estão na{" "}
              <button className="link-brand" onClick={() => setAba("lista")}>Lista</button>.
              Nenhum tem ponto marcado ainda: ao publicar, toque em “Onde foi?” e
              marque no mapa pra ele virar pino aqui.
            </p>
          </Empty>
        ) : (
          /* Sem `.card` em volta: o mapa sangra até as bordas e um cartão
             por cima devolveria a margem que a caixa acabou de tirar. */
          <Mapa places={comMapa} />
        )}

        {/* O respiro do fim não vale para o mapa, que já ocupa o que sobra. */}
        {aba !== "mapa" && <div className="gap" />}
      </div>

      <TabBar cid={cid} />
    </main>
  );
}

function Mapa({ places }: { places: Place[] }) {
  const caixa = useRef<HTMLDivElement>(null);
  const mapa = useRef<LeafletMap | null>(null);
  const [morto, setMorto] = useState(false);
  const [pronto, setPronto] = useState(false);

  useEffect(() => {
    let vivo = true;

    (async () => {
      try {
        const L = await import("leaflet");
        await import("leaflet/dist/leaflet.css");
        if (!vivo || !caixa.current) return;

        /* As fotos ANTES de desenhar: `photoURL` é uma ida ao Storage por
           caminho (com cache), e criar o marcador sem ela faria o pino
           nascer vazio e trocar de cara depois. */
        const fotos = await Promise.all(
          places.map((p) => photoURL(p.thumbPath).catch(() => null)),
        );
        if (!vivo || !caixa.current) return;

        const m = L.map(caixa.current, { attributionControl: true, scrollWheelZoom: false });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19, attribution: "&copy; OpenStreetMap",
        }).addTo(m);

        const pontos: [number, number][] = places.map((p) => [p.coords!.lat, p.coords!.lng]);
        m.fitBounds(pontos, { padding: [40, 40], maxZoom: 16 });

        places.forEach((p, i) => {
          const foto = fotos[i];

          /* O pino é a FOTO do prato, não um ponto genérico: o documento do
             lugar já guarda `thumbPath`, então isto não custa dado novo.
             Sem foto ainda dá um pino, só que liso. `divIcon` em vez dos
             PNGs de marcador do Leaflet, que o bundler não copia sozinho.

             E é montado no DOM, não por string de HTML. A primeira versão
             passava a URL do Storage por `encodeURI` e isso a CORROMPIA:
             a URL de download já vem codificada, com o caminho em `%2F`,
             e `encodeURI` escapa o `%` de novo — `%2F` virava `%252F`. A
             imagem dava 404 e TODO pino do mapa saía como um círculo
             vazio. Atribuir `img.src` não reescapa nada, e de quebra não
             sobra string de HTML onde algo possa se infiltrar. */
          const raiz = document.createElement("div");

          const moldura = document.createElement("span");
          moldura.className = "pino-foto";
          if (foto) {
            const img = document.createElement("img");
            img.src = foto;
            img.alt = "";
            moldura.append(img);
          } else {
            const liso = document.createElement("span");
            liso.className = "pino-sem";
            moldura.append(liso);
          }

          const conta = document.createElement("i");
          conta.className = "pino-conta";
          conta.textContent = p.visits > 99 ? "99+" : String(p.visits);

          const bico = document.createElement("span");
          bico.className = "pino-bico";

          raiz.append(moldura, conta, bico);

          const icone = L.divIcon({
            className: "pino-lugar",
            html: raiz,
            iconSize: [46, 56],
            iconAnchor: [23, 56],
            popupAnchor: [0, -52],
          });

          /* O popup é montado com nós de texto, não com string de HTML.
             O nome do lugar é digitado por quem posta (ou vem do
             OpenStreetMap), e antes ia cru para dentro do `bindPopup` —
             um nome com marcação virava marcação de verdade. */
          const corpo = document.createElement("div");
          const nome = document.createElement("strong");
          nome.textContent = p.name;
          corpo.append(nome, document.createElement("br"));
          corpo.append(document.createTextNode(
            `${p.visits} ${p.visits === 1 ? "visita" : "visitas"}`,
          ));
          if (p.rating != null) {
            corpo.append(document.createElement("br"));
            corpo.append(document.createTextNode(`⭐ ${formatRating(p.rating)}`));
          }

          L.marker([p.coords!.lat, p.coords!.lng], { icon: icone, title: p.name })
            .addTo(m)
            .bindPopup(corpo);
        });

        mapa.current = m;
        setPronto(true);
        setTimeout(() => m.invalidateSize(), 60);
      } catch {
        if (vivo) setMorto(true);
      }
    })();

    return () => { vivo = false; mapa.current?.remove(); mapa.current = null; };
  }, [places]);

  if (morto) {
    return (
      <div className="map-box">
        <div className="picker-empty">
          <strong>Mapa indisponível</strong><br />
          Os mapas precisam de internet. A lista continua funcionando.
        </div>
      </div>
    );
  }

  return (
    <div className="map-box">
      <div ref={caixa} style={{ width: "100%", height: "100%" }} />
      {!pronto && <Spinner />}
    </div>
  );
}
